'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import { ChevronRight, Minus, Plus } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { cn } from '~/lib/utils';

import { replaceAsteriskedTextWithFancyUnderline } from '../fancy-underline';
import { PreloadedClassyIframe } from '../preloaded-classy-iframe';
import truckStyles from './delivery-truck.module.css';
import s from './falling-gifts.module.css';
import kidStyles from './kid-profiles.module.css';

type FallingGift = {
  id: number;
  name: string;
  image: string;
  style: Partial<
    React.CSSProperties & {
      '--translate-x': string;
      '--rotate-from': string;
      '--rotate-to': string;
    }
  >;
};

const FALLING_GIFTS_DATA: FallingGift[] = [
  {
    id: 1,
    name: 'Gift 1',
    image: '/img/give-happiness/gift-1.svg',
    style: {
      left: '10%',
      '--translate-x': '20px',
      '--rotate-from': '0deg',
      '--rotate-to': '360deg',
    },
  },
  {
    id: 2,
    name: 'Gift 2',
    image: '/img/give-happiness/gift-2.svg',
    style: {
      left: '60%',
      '--translate-x': '-15px',
      '--rotate-from': '45deg',
      '--rotate-to': '-270deg',
    },
  },
  {
    id: 3,
    name: 'Gift 3',
    image: '/img/give-happiness/gift-3.svg',
    style: {
      left: '85%',
      '--translate-x': '10px',
      '--rotate-from': '-30deg',
      '--rotate-to': '180deg',
    },
  },
];

function FallingGifts() {
  return (
    <div
      className={cn('pointer-events-none absolute inset-0', s.root)}
      style={
        {
          '--gift-count': FALLING_GIFTS_DATA.length,
        } as React.CSSProperties
      }
    >
      {FALLING_GIFTS_DATA.map((gift, index) => (
        <div
          key={gift.id}
          className={cn('absolute -z-20', s.gift)}
          style={{ '--index': index, ...gift.style } as React.CSSProperties}
        >
          <Image
            alt={gift.name}
            className='-z-10 object-contain object-center'
            draggable='false'
            fill
            src={gift.image}
          />
        </div>
      ))}
    </div>
  );
}

function DeliveryTruck() {
  return (
    <div className={cn(truckStyles.truck, 'lg:h-30 h-20 w-40 lg:w-60')}>
      <Image
        alt='Starlight Delivery Truck'
        src='/img/give-happiness/delivery-truck.svg'
        fill
        className='size-full object-contain'
        draggable='false'
      />
    </div>
  );
}

const allKids = [
  {
    name: 'Arcelia',
    image: '/img/give-happiness/kids/arceliam.jpg',
    bio: "During the second week of December, Jessica was looking forward to buying her twin daughters presents for their first Christmas.\n\nHer plans were abruptly disrupted though when Arcelia, one of her one-year-old twins, was suddenly admitted to the hospital on December 12th, 2024.\n\nJessica was disheartened. She wanted to stay by Arcelia's side and wanted to ensure she had a meaningful first Christmas.\n\nBut how could she do that if Jessica couldn't go gift shopping?\n\nHope arrived when she received news of good cheer: Arcelia would be receiving gifts this year!\n\nA nurse came in with a gift bag filled with toys for Arcelia that the hospital received through the Starlight Toy Deliveries program. Jessica was also invited to attend a hospital event where parents could go and pick gifts for their children — she would be able to select gifts for Arcelia after all.",
  },
  {
    name: 'Bella',
    image: '/img/give-happiness/kids/bellac.jpg',
    bio: 'Bella was born with closed circuit gastroschisis, meaning that part of her small intestine was outside of her body. When a fever meant a hospital admittance, Bella was bored. She was uncomfortable. She missed her family. She wanted to go home.\n\nBut the following Tuesday, A Child Life Specialist came into her room and asked, "Do you want to go to the playroom? There\'s a Ghostbusters event going on."\n\nAs she entered, Bella was met by the Ghostbusters, proton packs strapped on their backs, Ecto-wands in hand. Bella shared her story with the Ghostbusters. "It was so fun to talk about me, and then they gave me these gifts," she said, pointing to her Starlight Toy Deliveries - Slimer and Marshmallow man keychains her favorite characters!\n\nBella was also gifted a Starlight Hospital Gown. She instantly loved it! Its Ghostbusters design of a flight suit with slime brightened Bella\'s mood, too. "It\'s going to boost my mood because even though it\'s a hospital gown, it\'s just way funner," Bella said.',
  },
  {
    name: 'Devon',
    image: '/img/give-happiness/kids/devon.png',
    bio: "For childhood cancer patients like Devon, separation from loved ones increases feelings of isolation and anxiety, making it more challenging to be resilient during painful treatments and procedures.\n\nBut when Devon's child life specialist gave him a Starlight Hospital Gown, it completely changed Devon's hospital experience. Replacing his plain regular hospital gown, his Starlight Hospital Gown was brightly colored, comfortable and had more coverage.\n\nUnlike regular gowns, Devon's Starlight Gown tied down the side and had plastic snaps on each shoulder, which gave easier access for medical procedures and made it less painful for Devon, too.",
  },
  {
    name: 'Easton',
    image: '/img/give-happiness/kids/easton.jpg',
    bio: "Because of long hospital stays and his cancer treatments, Easton couldn't attend school. With dreams of attending college and a love for learning, he was worried about falling behind in his course work.\n\nLindy, Easton's mom, says, \"Easton was frustrated. He looked at me and said, 'Mom, I'm going to get left behind.' It almost broke my heart. It was really hard on him.\"\n\nStaying connected to classmates was difficult too. For kids like Easton, that distance can make feelings of loneliness and isolation even harder. Although Easton wasn't there in person, his classmates and teachers made a special effort to let him know they were thinking of him.\n\nEaston's story is a testament to why community support is so powerful. Kids knowing they're being cheered on can have a lasting impact on a child's healing process. Starlight's community is coming together to show hospitalized kids nationwide that they're not alone.",
  },
  {
    name: 'Emily',
    image: '/img/give-happiness/kids/emilya.jpg',
    bio: "12-year-old Emily has developed extreme fear and anxiety related to hospitals, so much so that she starts crying even before her family pulls into the hospital's parking structure.\n\nShe lives with a genetic deletion that causes medical complexities, including deafness, blindness, severe apnea, and needs oxygen and a g-tube. Emily also has autism and is nonverbal.\n\nHospital visits, needle pokes, infusions and surgery are a routine part of life for Emily, but that doesn't make them any easier.\n\nTo help ease the stress, Emily's family finds little ways to bring her comfort - such as taking rides in the medically accessible Starlight Radio Flyer Hero Wagon, a proven soothing experience for Emily. The wagon is Emily's constant companion in the hospital. From the moment she wakes up to when she goes to bed, Emily wants to be in the wagon. Judy spends 8-12 hours a day pulling her in the wagon, walking laps around the pediatric unit.",
  },
  {
    name: 'Hiro',
    image: '/img/give-happiness/kids/hiro.jpg',
    bio: 'Constant visits to the hospital, especially during the school year, can be quite stressful for Kalyn and her family. Hiro, her youngest of five children, was born with a unique limb structure and uses prosthetic legs as part of his daily life.\n\nHiro\'s mom, Kalyn, shares that the prosthetic fitting process often requires multiple visits to the hospital each week, with adjustments happening step-by-step. It\'s time-consuming and physically demanding - they spend many hours at the clinic ensuring everything fits just right.\n\nWhen Hiro is hospitalized, he worries about missing classroom instruction. He\'s a driven, bright, and avid learner, striving to be at the top of his class. Kalyn expressed the stress of missing school for Hiro, including feeling like he was going to fall behind and have more to do to catch up.\n\nDuring these times, Kalyn and Hiro have sometimes tapped into the resources the hospital provides to support families during the school year. "They have school supplies ... they really made a difference," Kalyn reminisced. "They\'re always cute, fun things that Hiro likes."',
  },
  {
    name: 'Jade',
    image: '/img/give-happiness/kids/jadew.jpg',
    bio: "11-year-old Jade, wanted to give up, she was staying at the hospital due to an incurable condition – Pulmonary Veno Occlusive Disease, the most rare and severe form of pulmonary hypertension. The only treatment option was a double lung transplant, and Jade apprehensively waited in the ICU for four months after her diagnosis until she received her transplant.\n\nStaying in the hospital was not an easy feat for Jade. Staying in an unfamiliar place, coupled with fearing daunting procedures, makes it more difficult for kids to feel emotionally and mentally well.\n\nBut when Jade felt anxious or wanted to give up, her mom looked for ways to brighten Jade's spirits through play. Starlight programs integrated play in her hospital stay through gowns featuring her favorite games and characters. These programs, together, helped provide positive distraction to help Jade cope in the hospital.",
  },
  {
    name: 'Jameson',
    image: '/img/give-happiness/kids/jameson.jpg',
    bio: "Nine-year-old Jameson is in his third battle with cancer, relapsing with high-risk pre-B cell acute lymphoblastic leukemia.\n\n\"We were in the hospital one night, and he said, 'Mom, it's okay, we can do hard things,'\" shared Noelle, Jameson's mom.\n\nWhen he and Noelle heard he needed to get an MRI, it was another hard thing he would have to face — he had never gotten an MRI done before.\n\nA Child Life Specialist brought in a helpful tool to make the process easier for Jameson — the LEGO MRI Scanner set.\n\nAlmost immediately, Jameson felt more at ease playing. Their Child Life Specialist simultaneously played a video that educated Jameson about the mechanisms of a machine while he was playing with it.\n\nJameson wasn't so nervous anymore. He entered the MRI with courage and confidence.\n\nHe completed the MRI successfully and did so well that the hospital staff said they had never seen a child do so well in the MRI machine.",
  },
  {
    name: 'Rezen',
    image: '/img/give-happiness/kids/rezen.jpg',
    bio: "10-year-old Rezen celebrated her birthday in her hospital room at Kapi'olani Medical Center for Women & Children in Honolulu, her home for more than a year.\n\nDiagnosed with severe aplastic anemia – a rare and life-threatening blood disorder.\n\nWhen Rezen was nervous before an MRI, one of Kapi'olani's Child Life Specialists gave her a Disney Princess-themed Starlight Hospital Gown with matching pants, transforming her day.\n\nRare pediatric diseases come with complex psychosocial challenges that can affect the entire family. These children often endure a long and challenging diagnostic journey, with some undergoing years of medical tests and consultations with multiple specialists. Frequent hospitalizations and the need for in-home care place strain on children and their families as they navigate complex medical needs.\n\nStarlight gowns are designed to support a child's mental well-being from the start of their hospital stay. Kids love wearing them because they help ease anxiety by enabling greater privacy and offering more comfort while allowing kids to express individuality.",
  },
  {
    name: 'Shaldon',
    image: '/img/give-happiness/kids/shaldon.jpg',
    bio: "Shaldon was diagnosed with a rare epileptic condition called febrile infection-related epilepsy syndrome (FIRES). There is no definitive information on how to treat his condition.\n\nAt one point, Shaldon was having more than 150 seizures a day.\n\nNow, over a year later, he's doing better but still requires care 24/7. Shaldon and his family are continuing to face the daily challenges that come with serious illnesses.\n\n\"He's completely machine dependent, wheelchair dependent,\" shares Tatiana.\n\nCommunity support has brought moments of comfort and joy to Shaldon's family. One of these moments came when Shaldon received a Starlight character-themed kids Hospital Gown. Unlike standard gowns, Starlight Gowns are more vibrant, colorful and designed to let kids express themselves at no cost to families.\n\nWhen families are facing their toughest moments, a moment of comfort can be the warm hug they need to keep enduring, keep hoping, and keep persevering. A serious illness changes everything for a family — but with your help, so can a single moment of happiness.",
  },
  {
    name: 'Sophia',
    image: '/img/give-happiness/kids/sophias.jpg',
    bio: "4-year-old Sophia was diagnosed with leukemia in June 2024 - a moment that shattered her family's world. Prior to her diagnosis, Sophia was a healthy, active child, always running around, laughing, and playing. However, her family began to notice concerning signs—Sophia's gait changed, she cried in pain, and she refused to walk.\n\nSophia's atypical behaviors led them to seek medical help, and they soon learned that their sweet and vibrant daughter had leukemia. Sophia's hospital visits are often comprised of long waits and tough moments.\n\nSo, when Sophia discovered the Starlight Nintendo Switch Gaming Station in the infusion room, she was amazed. She could play Mario Kart, which is such a fun and positive distraction that she once forgot she was there for treatment!",
  },
  {
    name: 'Trey',
    image: '/img/give-happiness/kids/treyl.jpg',
    bio: 'After a visit to the ER, a mass was discovered on Trey\'s brain, resulting in a seven-hour surgery to remove it. Two-year-old Trey was diagnosed with stage 4 atypical teratoid/rhabdoid tumor (ATRT), a rare form of brain cancer.\n\n"Trey continues to fight and find the strength through it all. He has a very outgoing personality and shines! Not even cancer can take that away from him!"\n\nThroughout his journey, Starlight Hospital Gowns encouraged Trey and Ebony\'s strength and courage to keep fighting and aided in easing anxiety during hospitalizations.\n\n"Starlight Gowns have supported Trey during very challenging times," Ebony told us. "They\'ve helped him display his inner strength despite facing daunting moments during treatment. Not even cancer can keep him from looking his best and fighting with a smile!"',
  },
];

function useRunOnce(effect: () => void) {
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    effect();
  }, []);
}

export function GiveHappinessHero() {
  const [step, setStep] = useState(1);
  const [kidCount, setKidCount] = useState(3);
  const donationAmount = kidCount * 25; // $25 per kid

  const [kids, setKids] = useState<Array<{ name: string; image: string; bio?: string }>>([]);

  useRunOnce(() => {
    const shuffledKids = [...allKids].sort(() => Math.random() - 0.5);
    setKids([...shuffledKids.slice(0, 5), { name: 'and more!', image: '/svg/and-more-star.svg' }]);
  });

  const incrementKids = () => setKidCount((prev) => prev + 1);
  const decrementKids = () => setKidCount((prev) => Math.max(prev - 1, 1));

  return (
    <div className='-mt-(--header-margin-bottom)'>
      <div className='lg:flex lg:flex-row'>
        {/* Left Side - Step Content */}
        <div className='bg-blue-light/15 flex flex-col items-center justify-center px-5 pb-12 pt-10 text-center lg:w-1/2 lg:shrink-0 lg:px-8 lg:py-14'>
          {step === 1 ? (
            <>
              <div className='mb-4 flex items-center lg:mb-6'>
                <Image src='/svg/give-happiness.svg' alt='Give Happiness Logo' width={223.78} height={48} />
              </div>

              <h2 className='display-sm relative z-10 mx-auto max-w-lg text-balance'>
                {replaceAsteriskedTextWithFancyUnderline('How many kids can *you* help?', false)}
              </h2>

              <div className='mb-4 mt-6 flex items-center gap-x-8 lg:mb-4 lg:mt-8 '>
                <Button
                  className='bg-blue-light flex size-8 items-center justify-center rounded-full p-4 text-white md:size-10 xl:size-14 xl:p-3'
                  onClick={decrementKids}
                  disabled={kidCount <= 1}
                >
                  <Minus size={16} strokeWidth={6} className='shrink-0 text-white xl:size-5' />
                </Button>

                <div className='font-greycliff-extrabold text-blue min-w-32 text-8xl tracking-tight lg:min-w-48 lg:text-9xl xl:min-w-72 xl:text-[12rem] '>
                  {kidCount}
                </div>

                <Button
                  className='bg-blue-light flex size-8 items-center justify-center rounded-full p-4 text-white md:size-10 xl:size-14 xl:p-3'
                  onClick={incrementKids}
                >
                  {/* TODO: These icons arent the mock ups version */}
                  <Plus size={16} strokeWidth={6} className='shrink-0 text-white xl:size-5' />
                </Button>
              </div>

              <div className='flex w-full max-w-sm items-center justify-center rounded-full bg-white p-1 max-sm:w-full'>
                <p className='font-greycliff-extrabold min-w-41 flex h-full items-center rounded-l-full bg-white pl-6 pr-2'>
                  I will give ${donationAmount}
                </p>
                <Button className='grow' onClick={() => setStep(2)}>
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Step 2 - Classy Embed (preloaded iframe now visible) */}
              <div className='w-full'>{/* The preloaded iframe is now shown */}</div>
            </>
          )}

          {/* Single iframe that preloads in step 1 and shows in step 2 */}
          <PreloadedClassyIframe
            block={{ headline: 'Complete Your Donation', campaignId: '729262' }}
            amount={donationAmount}
            isVisible={step === 2}
            shouldPreload={step === 1}
          />
        </div>
        {/* Right Side - Kids Preview */}
        <div className='bg-blue-light/40 relative overflow-hidden px-5 pt-10 lg:w-1/2 lg:shrink-0 lg:px-8 lg:pt-16'>
          <FallingGifts />

          <div className='lg:pb-35 relative z-10 flex h-full flex-col pb-24'>
            {/* Header text */}
            <h2 className='heading-sm mb-6 text-center lg:mb-12'>You will send toys to {kidCount} kids like:</h2>

            {/* Kids profiles */}
            <div className='shink-0 min-h-68 xl:min-h-100 mx-auto flex grow items-center justify-center max-md:max-w-sm lg:min-h-72'>
              <div
                className={cn(
                  'mx-auto flex w-full max-w-sm flex-wrap justify-center gap-4 lg:gap-x-12  lg:gap-y-8 xl:max-w-xl',
                  kidStyles.kidProfileContainer,
                )}
              >
                {kids.slice(0, Math.min(kidCount, 6)).map((kid, index) => {
                  const kidProfile = (
                    <div
                      className={cn(
                        'text-center transition-all',
                        kidStyles.kidProfile,
                        kid.bio && 'cursor-pointer hover:scale-105',
                      )}
                    >
                      <div
                        className={cn(
                          'mb-2 overflow-hidden md:mb-3',
                          kid.name === 'and more!' ? 'border-0' : 'xl:border-6 rounded-full border-4 border-white',
                        )}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={kid.image || '/placeholder.svg'}
                          alt={kid.name}
                          className={cn(
                            'aspect-square size-full',
                            kid.name === 'and more!' ? 'object-contain p-3' : 'object-cover',
                          )}
                        />
                      </div>
                      <p className='font-greycliff-extrabold xl:text-xl'>{kid.name}</p>
                    </div>
                  );

                  if (!kid.bio) {
                    return (
                      <div className='w-23 xl:w-36' key={`${kid.name}-${index}`}>
                        {kidProfile}
                      </div>
                    );
                  }

                  return (
                    <Dialog key={`${kid.name}-${index}`}>
                      <DialogTrigger asChild>
                        <button className='w-23 xl:w-36'>{kidProfile}</button>
                      </DialogTrigger>
                      <DialogContent className='lg:[&>button>svg]:text-foreground max-h-[90vh] gap-y-5 overflow-y-auto rounded-2xl p-6 sm:max-w-2xl lg:px-14 lg:py-10'>
                        <div className='flex items-center justify-between gap-4'>
                          <div>
                            <Image
                              src='/svg/shining-star-yellow.svg'
                              width={28.26}
                              height={32}
                              alt='Shining Star Icon'
                              className='mb-3 h-6 w-auto '
                            />

                            <DialogTitle className='font-greycliff-extrabold text-foreground text-2xl sm:text-4xl'>
                              Meet {kid.name}!
                            </DialogTitle>
                          </div>
                          <div className='size-20 shrink-0 overflow-hidden rounded-full sm:size-32'>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={kid.image} alt={kid.name} className='h-full w-full object-cover' />
                          </div>
                        </div>
                        <div className='body-lg space-y-4'>
                          {kid.bio?.split('\n').map((paragraph, i) => (
                            <p key={i}>{paragraph}</p>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>
                  );
                })}
              </div>
            </div>
          </div>

          <DeliveryTruck />
        </div>
      </div>
    </div>
  );
}
